package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.geom.Handle
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.geom.SnapResult
import dev.lingvo.shared.screenshots.geom.SnapTargets
import dev.lingvo.shared.screenshots.geom.aabb
import dev.lingvo.shared.screenshots.geom.worldOf
import dev.lingvo.shared.screenshots.geom.apply
import dev.lingvo.shared.screenshots.render.Affine

/** The editor's injectable geometry boundary uses W2-03's document and snap signatures. */
internal interface EditorGeometry {
    fun world(page: Page, id: String): Affine?
    fun bounds(page: Page, layer: Layer): Rect
    fun hitTest(layers: List<Layer>, point: Point, tolerance: Double): String?
    fun targets(page: Page, board: Board, exclude: Set<String>, safe: Rect?): SnapTargets
    fun snapMove(rect: Rect, delta: Point, targets: SnapTargets, threshold: Double): SnapResult
    fun snapResize(rect: Rect, handle: Handle, delta: Point, targets: SnapTargets, threshold: Double, aspect: Boolean): SnapResult
    fun snapAngle(degrees: Double, forced: Boolean): Double
}

/** Production uses the merged engine implementation; test fakes implement only this boundary. */
internal object EngineEditorGeometry : EditorGeometry {
    override fun world(page: Page, id: String): Affine? = worldOf(page, id)
    override fun bounds(page: Page, layer: Layer): Rect {
        val matrix = requireNotNull(world(page, layer.base.id))
        val box = layer.base.box
        val points = listOf(Point(0.0, 0.0), Point(box.w, 0.0), Point(0.0, box.h), Point(box.w, box.h)).map { matrix.apply(it) }
        val x = points.minOf { it.x }; val y = points.minOf { it.y }
        return Rect(x, y, points.maxOf { it.x } - x, points.maxOf { it.y } - y)
    }
    override fun hitTest(layers: List<Layer>, point: Point, tolerance: Double): String? =
        dev.lingvo.shared.screenshots.geom.hitTest(layers, point, tolerance)
    override fun targets(page: Page, board: Board, exclude: Set<String>, safe: Rect?): SnapTargets =
        dev.lingvo.shared.screenshots.geom.snapTargets(page, board, exclude, null, safe)
    override fun snapMove(rect: Rect, delta: Point, targets: SnapTargets, threshold: Double): SnapResult =
        dev.lingvo.shared.screenshots.geom.snapMove(rect, delta, targets, threshold)
    override fun snapResize(rect: Rect, handle: Handle, delta: Point, targets: SnapTargets, threshold: Double, aspect: Boolean): SnapResult =
        dev.lingvo.shared.screenshots.geom.snapResize(rect, handle, delta, targets, threshold, aspect)
    override fun snapAngle(degrees: Double, forced: Boolean): Double =
        editorSnapAngle(degrees, forced)
}

/** Cache bounds include paint outside the nominal frame rather than silently cropping the preview. */
internal fun editorRasterBounds(page: Page, layer: Layer): Rect {
    var bounds = EngineEditorGeometry.bounds(page, layer)
    if (layer is dev.lingvo.shared.screenshots.doc.GroupLayer && !layer.clip) {
        layer.children.filterNot { it.base.hidden }.forEach { child ->
            val childBounds = editorRasterBounds(page, child)
            val x = minOf(bounds.x, childBounds.x)
            val y = minOf(bounds.y, childBounds.y)
            bounds = Rect(x, y, maxOf(bounds.right, childBounds.right) - x, maxOf(bounds.bottom, childBounds.bottom) - y)
        }
    }
    if (layer is dev.lingvo.shared.screenshots.doc.ShapeLayer && layer.points.isNotEmpty()) {
        val world = requireNotNull(EngineEditorGeometry.world(page, layer.base.id))
        val points = layer.points.map { world.apply(it) }
        val x = minOf(bounds.x, points.minOf { it.x })
        val y = minOf(bounds.y, points.minOf { it.y })
        bounds = Rect(x, y, maxOf(bounds.right, points.maxOf { it.x }) - x, maxOf(bounds.bottom, points.maxOf { it.y }) - y)
    }
    val stroke = when (layer) {
        is dev.lingvo.shared.screenshots.doc.ShapeLayer -> layer.stroke?.width ?: 0.0
        is dev.lingvo.shared.screenshots.doc.ImageLayer -> layer.stroke?.width ?: 0.0
        else -> 0.0
    }
    val shadow = layer.base.shadow
    val padding = 2 + stroke + (shadow?.blur ?: 0.0) * 3 + kotlin.math.abs(shadow?.dx ?: 0.0) + kotlin.math.abs(shadow?.dy ?: 0.0)
    return Rect(bounds.x - padding, bounds.y - padding, bounds.w + 2 * padding, bounds.h + 2 * padding)
}
